import type { WebAgentCustomInstruction } from '@circuitry-ai/doc-data';
import { CtyButton } from '@circuitry-ai/doc-ui';
import { Input, Select, SelectItem } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';

const CUSTOM_OPS = ['rename', 'merge'] as const;
const MATCH_MODES = ['exact', 'prefix', 'suffix', 'contains', 'regex'] as const;
const AS_MODES = ['list', 'object'] as const;

function EnumSelect({ ariaLabel, className, value, options, isReadOnly, onChange }: Readonly<{
  ariaLabel: string; className: string; value: string;
  options: readonly string[]; isReadOnly?: boolean; onChange: (value: string) => void;
}>) {
  return (
    <Select
      aria-label={ariaLabel} size="sm" className={className}
      selectedKeys={new Set([value])} disallowEmptySelection isDisabled={isReadOnly}
      onSelectionChange={(keys) => onChange(String([...keys][0]))}
    >
      {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
    </Select>
  );
}

export type CustomInstructionRowProps = Readonly<{
  instruction: WebAgentCustomInstruction;
  index: number;
  total: number;
  isReadOnly?: boolean;
  onChange: (partial: Partial<WebAgentCustomInstruction>) => void;
  onMove?: (dir: -1 | 1) => void;
  onRemove?: () => void;
}>;

export function CustomInstructionRow({ instruction, index, total, isReadOnly, onChange, onMove, onRemove }: CustomInstructionRowProps) {
  const { t } = useTranslation();
  const op = String(instruction?.op ?? 'rename');
  const sources = Array.isArray(instruction?.sources) ? instruction.sources : [];

  const setSource = (i: number, value: string) => onChange({ sources: sources.map((s, idx) => (idx === i ? value : s)) });
  const addSource = () => onChange({ sources: [...sources, ''] });
  const removeSource = (i: number) => onChange({ sources: sources.filter((_, idx) => idx !== i) });

  const fromMatchRow = (label: string, placeholder: string) => (
    <div className="flex gap-2 items-center">
      <Input
        size="md" aria-label="custom_from" className="flex-1" label={label} labelPlacement="outside-left"
        value={String(instruction?.from ?? '')} isReadOnly={isReadOnly} placeholder={placeholder}
        onValueChange={(v) => onChange({ from: v })}
      />
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-sm text-foreground whitespace-nowrap">{t('Match')}</span>
        <EnumSelect
          ariaLabel="custom_match" className="w-32" value={String(instruction?.match ?? 'exact')}
          options={MATCH_MODES} isReadOnly={isReadOnly} onChange={(v) => onChange({ match: v as WebAgentCustomInstruction['match'] })}
        />
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-2 border border-default-200 rounded-md p-3">
      <div className="flex items-center gap-2">
        <span className="text-xs text-default-400 w-6">{index + 1}</span>
        <EnumSelect ariaLabel="custom_op" className="w-36" value={op} options={CUSTOM_OPS} isReadOnly={isReadOnly} onChange={(v) => onChange({ op: v })} />
        <div className="flex-1" />
        {!isReadOnly && (onMove || onRemove) && (
          <>
            {onMove && <CtyButton isIconOnly size="sm" variant="light" aria-label="move_up" isDisabled={index === 0} onPress={() => onMove(-1)}><Icon icon="mdi:arrow-up" /></CtyButton>}
            {onMove && <CtyButton isIconOnly size="sm" variant="light" aria-label="move_down" isDisabled={index === total - 1} onPress={() => onMove(1)}><Icon icon="mdi:arrow-down" /></CtyButton>}
            {onRemove && <CtyButton isIconOnly size="sm" variant="light" color="danger" aria-label="remove_instruction" onPress={onRemove}><Icon icon="mdi:trash-can-outline" /></CtyButton>}
          </>
        )}
      </div>

      {op === 'rename' && (
        <>
          {fromMatchRow(t('From'), 'Claim Information - ${claimNumber}')}
          <Input
            size="sm" aria-label="rename_to" label={t('To')} labelPlacement="outside-left"
            value={String(instruction?.to ?? '')} isReadOnly={isReadOnly} placeholder={t('New key name')}
            onValueChange={(v) => onChange({ to: v })}
          />
        </>
      )}

      {op === 'merge' && (
        <>
          {fromMatchRow(t('From (pattern)'), 'Failure Part No')}
          <div className="flex flex-col gap-1">
            <span className="text-xs text-default-500">{t('Sources (exact section names; used only when From is empty)')}</span>
            {sources.map((source, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={i} className="flex gap-2 items-center">
                <Input
                  size="sm" aria-label={`merge_source_${i}`} className="flex-1"
                  value={String(source ?? '')} isReadOnly={isReadOnly} placeholder={t('Section name')}
                  onValueChange={(v) => setSource(i, v)}
                />
                {!isReadOnly && (
                  <CtyButton isIconOnly size="sm" variant="light" color="danger" aria-label="remove_source" onPress={() => removeSource(i)}>
                    <Icon icon="mdi:trash-can-outline" />
                  </CtyButton>
                )}
              </div>
            ))}
            {!isReadOnly && (
              <CtyButton size="sm" variant="bordered" className="self-start" startContent={<Icon icon="mdi:plus" />} onPress={addSource}>
                {t('Add source')}
              </CtyButton>
            )}
          </div>
          <div className="flex gap-2 items-center">
            <Input
              size="md" aria-label="merge_into" className="flex-1" label={t('Into')} labelPlacement="outside-left"
              value={String(instruction?.into ?? '')} isReadOnly={isReadOnly} placeholder={t('Parent section name')}
              onValueChange={(v) => onChange({ into: v })}
            />
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-sm text-foreground whitespace-nowrap">{t('As')}</span>
              <EnumSelect
                ariaLabel="merge_as" className="w-32" value={instruction?.as === 'object' ? 'object' : 'list'}
                options={AS_MODES} isReadOnly={isReadOnly} onChange={(v) => onChange({ as: v as WebAgentCustomInstruction['as'] })}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default CustomInstructionRow;
